/**
 * RouterWireguardPanel (rc148) — bloque WireGuard en la edición del router.
 *
 * Muestra el túnel WG si el router tiene peer vinculado y permite:
 *   - "Pasar a WireGuard": prueba la API RouterOS por la IP del túnel y SOLO
 *     si contesta cambia la IP de gestión (guarda la anterior de respaldo).
 *   - "Regresar a OpenVPN": vuelve a la IP de respaldo.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRightLeft, CheckCircle2, CircleAlert, Loader2, Undo2, Waypoints } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { useActivarWireguard, useRevertirWireguard, useRouterWireguard } from "@/features/vpn/wgApi";
import { haceCuanto, type RouterWireguardActivarResponse } from "@/features/vpn/wgSchemas";
import { EstadoBadge } from "@/features/vpn/WireguardPanel";

type Props = {
  routerId: string;
  routerNombre: string;
  /** Avisa la nueva IP de gestión para actualizar el formulario abierto. */
  onIpChange?: (ip: string) => void;
};

export function RouterWireguardPanel({ routerId, routerNombre, onIpChange }: Props) {
  const { data: info, isLoading } = useRouterWireguard(routerId);
  const activarMut = useActivarWireguard();
  const revertirMut = useRevertirWireguard();
  const [resultado, setResultado] = useState<RouterWireguardActivarResponse | null>(null);

  const peer = info?.peer ?? null;

  async function activar() {
    if (!peer) return;
    if (!confirm(
      `¿Pasar "${routerNombre}" a WireGuard?\n\n` +
      `ByteISP probará la API del router por ${peer.ip_tunel}. Solo si contesta, ` +
      `la IP de gestión cambia a ${peer.ip_tunel} y la actual (${info?.ip_address}) ` +
      "queda de respaldo para regresar a OpenVPN.",
    )) return;
    setResultado(null);
    try {
      const res = await activarMut.mutateAsync(routerId);
      setResultado(res);
      if (res.ok) {
        if (res.ip_nueva) onIpChange?.(res.ip_nueva);
        toast.success("Router en WireGuard", { description: res.mensaje });
      } else {
        toast.error("No se cambió nada", { description: res.mensaje });
      }
    } catch (e) {
      toast.error("No se pudo pasar a WireGuard", { description: (e as ApiError).message });
    }
  }

  async function revertir() {
    if (!info?.ip_ovpn_respaldo) return;
    if (!confirm(
      `¿Regresar "${routerNombre}" a OpenVPN?\n\n` +
      `La IP de gestión vuelve a ${info.ip_ovpn_respaldo}. El túnel WireGuard ` +
      "sigue configurado (puedes volver a pasarlo después).",
    )) return;
    setResultado(null);
    try {
      const res = await revertirMut.mutateAsync(routerId);
      onIpChange?.(res.ip_address);
      toast.success("Router de regreso en OpenVPN", { description: `IP de gestión: ${res.ip_address}` });
    } catch (e) {
      toast.error("No se pudo regresar a OpenVPN", { description: (e as ApiError).message });
    }
  }

  return (
    <div className="mt-3 rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <Waypoints size={13} /> Túnel WireGuard
            {info?.usando_wireguard && <Badge variant="default">en uso</Badge>}
          </div>
          <div className="text-[10px] text-muted-foreground">
            Convive con OpenVPN. ByteISP habla con el router por la IP de gestión.
          </div>
        </div>
        {isLoading && <Loader2 size={13} className="animate-spin text-muted-foreground" />}
      </div>

      {!isLoading && !peer && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Este router no tiene peer WireGuard. Créalo y vincúlalo en{" "}
          <Link to="/vpn?tab=wireguard" className="text-electric hover:underline">VPN › WireGuard</Link>
          , pega el script en el MikroTik y vuelve aquí.
        </p>
      )}

      {peer && (
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] md:grid-cols-4">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Estado</div>
            <EstadoBadge peer={peer} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">IP túnel</div>
            <span className="font-mono">{peer.ip_tunel}</span>
          </div>
          <div title={peer.last_handshake_at ? formatDateTime(peer.last_handshake_at) : undefined}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Último contacto</div>
            {haceCuanto(peer.last_handshake_at)}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Respaldo OpenVPN</div>
            <span className="font-mono">{info?.ip_ovpn_respaldo ?? "—"}</span>
          </div>
        </div>
      )}

      {(peer || info?.ip_ovpn_respaldo) && (
        <div className="mt-2.5 flex flex-wrap justify-end gap-2">
          {peer && !info?.usando_wireguard && (
            <Button
              type="button" size="sm" variant="outline" onClick={activar}
              disabled={activarMut.isPending || !peer.activo}
              title={!peer.activo ? "El peer está inactivo" : undefined}
            >
              {activarMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <ArrowRightLeft size={12} />}
              {activarMut.isPending ? "Probando…" : "Pasar a WireGuard"}
            </Button>
          )}
          {info?.ip_ovpn_respaldo && (
            <Button type="button" size="sm" variant="outline" onClick={revertir} disabled={revertirMut.isPending}>
              {revertirMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
              Regresar a OpenVPN
            </Button>
          )}
        </div>
      )}

      {resultado && (
        <div className={`mt-2 rounded-md border p-2 text-[11px] ${resultado.ok ? "border-success-soft bg-success-soft text-success" : "border-danger-soft bg-danger-soft text-danger"}`}>
          <div className="flex items-start gap-2">
            {resultado.ok ? <CheckCircle2 size={13} className="mt-0.5 shrink-0" /> : <CircleAlert size={13} className="mt-0.5 shrink-0" />}
            <div className="min-w-0">
              <div className="font-medium">{resultado.mensaje}</div>
              {resultado.prueba.success ? (
                <div className="font-mono text-[10px]">
                  {resultado.prueba.identity ?? "—"} · {resultado.prueba.modelo ?? "—"} · RouterOS {resultado.prueba.version_ros ?? "—"} · {resultado.prueba.latencia_ms} ms
                </div>
              ) : (
                <div className="break-all font-mono text-[10px]">{resultado.prueba.error ?? "Sin respuesta"}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
